import React, { useState, useContext } from "react";
import classes from "./Board.module.scss";
import cardClasses from "../Card/Card.module.scss";
import rovatelMarkTeal from "../../assets/rovatel-mark-teal.png";
import rovatelMarkWhite from "../../assets/rovatel-mark-white.png";
import IconVerticalEllipses from "../Icons/IconVerticalEllipses";
import EmptyBoard from "../EmptyBoard/EmptyBoard";
import Column from "../Column/Column";
import { ModalContext } from "../../store/modal-context";
import { BoardContext } from "../../store/board-context";
import { ThemeContext } from "../../store/theme-context";
import IconAddTaskMobile from "../../assets/svgs/IconAddTaskMobile";
import chevronDown from "../../assets/icon-chevron-down.svg";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

const Board = ({ boardId }) => {
  const [menuIsShown, setMenuIsShown] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeType, setActiveType] = useState(null);

  const { boards, sidebarOpen, setSidebarOpen, createNewColumn, reorderColumns, moveTask } =
    useContext(BoardContext);
  const { openModal } = useContext(ModalContext);
  const { theme } = useContext(ThemeContext);
  const logoMark = theme === "_dark" ? rovatelMarkWhite : rovatelMarkTeal;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const board = boards.find((b) => b.id === boardId);

  if (!board) return null;

  const showBoardMenu = () => {
    setMenuIsShown(!menuIsShown);
  };

  const editBoard = () => {
    setMenuIsShown(false);
    openModal("editBoard", { boardId });
  };

  const deleteBoard = () => {
    setMenuIsShown(false);
    openModal("deleteBoard", { boardId });
  };

  const addNewTask = () => {
    openModal("addTask", { boardId });
  };

  const addNewColumn = () => {
    let columnName = prompt("Enter column name:");
    if (!columnName) return;

    columnName = columnName.trim();

    if (!columnName || columnName.length > 30) {
      alert("Please enter a valid column name (max 30 characters).");
      return;
    }

    createNewColumn(boardId, columnName);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setActiveType(event.active.data.current?.type ?? null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveType(null);
    if (!over) return;

    const activeData = active.data.current;
    if (!activeData) return;

    if (activeData.type === "column") {
      let overColumnId = over.id;
      if (over.data.current?.type === "task") {
        overColumnId = over.data.current.columnId;
      }
      const sourceIndex = board.columns.findIndex((col) => col.id === active.id);
      const destinationIndex = board.columns.findIndex((col) => col.id === overColumnId);
      if (sourceIndex === -1 || destinationIndex === -1 || sourceIndex === destinationIndex) return;
      reorderColumns(boardId, sourceIndex, destinationIndex);
      return;
    }

    if (activeData.type === "task") {
      const sourceColumnId = activeData.columnId;
      const overData = over.data.current;
      let destinationColumnId;
      let destinationIndex;

      if (overData?.type === "column") {
        destinationColumnId = overData.columnId ?? over.id;
        const destColumn = board.columns.find((col) => col.id === destinationColumnId);
        destinationIndex = destColumn ? destColumn.tasks.length : 0;
      } else if (overData?.type === "task") {
        destinationColumnId = overData.columnId;
        const destColumn = board.columns.find((col) => col.id === destinationColumnId);
        destinationIndex = destColumn ? destColumn.tasks.findIndex((t) => t.id === over.id) : -1;
        if (destinationIndex === -1) destinationIndex = destColumn ? destColumn.tasks.length : 0;
      } else {
        return;
      }

      const sourceColumn = board.columns.find((col) => col.id === sourceColumnId);
      const currentIndex = sourceColumn?.tasks.findIndex((t) => t.id === active.id);
      if (sourceColumnId === destinationColumnId && currentIndex === destinationIndex) return;

      moveTask(boardId, active.id, sourceColumnId, destinationColumnId, destinationIndex);
    }
  };

  const activeTask =
    activeType === "task"
      ? board.columns.flatMap((col) => col.tasks).find((t) => t.id === activeId)
      : null;
  const activeColumn =
    activeType === "column" ? board.columns.find((col) => col.id === activeId) : null;

  const columnIds = board.columns.map((col) => col.id);

  const boardContent = board.columns.length ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        <section className={classes.columns}>
          {board.columns.map((column) => (
            <Column key={column.id} boardId={boardId} column={column} />
          ))}
          <div
            className={`${classes.create_new_column}`}
          >
            <span onClick={addNewColumn}>+New Column</span>
          </div>
        </section>
      </SortableContext>
      <DragOverlay>
        {activeTask && (
          <section className={cardClasses.card}>
            <h3 className={cardClasses.task}>{activeTask.title}</h3>
            <p className={cardClasses.subTask}>
              {activeTask.subTasks.filter((st) => st.isCompleted).length} of{" "}
              {activeTask.subTasks.length} subtasks
            </p>
          </section>
        )}
        {activeColumn && (
          <h3 className={classes.dragOverlayColumn}>{activeColumn.name}</h3>
        )}
      </DragOverlay>
    </DndContext>
  ) : (
    <EmptyBoard createNewColumn={addNewColumn} />
  );

  return (
    <section className={classes.board_}>
      <section className={classes.header_}>
        <div className={`${classes.logo}`}>
          <img src={logoMark} alt="Rovatel" />
          <span className={classes.logoText}>Rovatel</span>
        </div>

        <div className={classes.header_text}>
          <button
            type="button"
            className={classes.header_mobile}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-expanded={sidebarOpen}
            aria-label="Switch board"
          >
            <img src={logoMark} alt="" />
            <span className={classes.platform_launch_text}>{board.title}</span>
            <img
              src={chevronDown}
              alt=""
              className={sidebarOpen ? classes.chevron_open : ""}
            />
          </button>

          <div className={classes.header_text_right}>
            <IconAddTaskMobile onClick={addNewTask} />
            <button className={classes.add_new_tasks} onClick={addNewTask}>
              + Add New Task
            </button>
            <IconVerticalEllipses showBoardMenu={showBoardMenu} />
            {menuIsShown && (
              <section
                className={`${classes.newBoard_menu}`}
              >
                <div
                  className={`${classes.newBoard_menu_content}`}
                >
                  <span className={classes.edit_board} onClick={editBoard}>
                    Edit Board
                  </span>
                  <span
                    className={classes.delete_board}
                    onClick={deleteBoard}
                  >
                    Delete Board
                  </span>
                </div>
              </section>
            )}
          </div>
        </div>
      </section>

      <section className={classes.kanban_app_body}>
        <section
          className={`${classes.kanban_body_content} ${
            sidebarOpen ? classes.kanban_body_content_shifted : ""
          }`}
        >
          {boardContent}
        </section>
      </section>
    </section>
  );
};

export default Board;
